import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { api } from '@/lib/api';
import type * as API from '@/types/api';
import { useAccountStore, useSubscriptionStore } from '@nimbitlabs/features';

interface EnvironmentsState {
  environments: API.EnvironmentDto[];
  loading: boolean;
  error: string | null;
  
  fetchEnvironments: () => Promise<void>;
  addEnvironment: (environmentData: API.CreateEnvironmentDto) => Promise<API.EnvironmentDto>;
  updateEnvironment: (id: string, environmentData: API.UpdateEnvironmentDto) => Promise<void>;
  deleteEnvironment: (environmentId: string) => Promise<void>;
  getEnvironmentById: (id: string) => Promise<API.EnvironmentDto>;
  hasReachedLimit: () => boolean;
  getEnvironmentLimit: () => { current: number; limit: number | null; hasReachedLimit: boolean };
  clearError: () => void;
}

const environmentsStore = create<EnvironmentsState>()(
  subscribeWithSelector((set, get) => ({
  environments: [],
  loading: false,
  error: null,
  
  fetchEnvironments: async () => {
    set({ loading: true, error: null });
    try {
      const data = await api.environments.getAll();
      set({ environments: data, loading: false });
    } catch (e: any) {
      set({ error: e?.message ?? 'Failed to fetch environments', loading: false });
    }
  },
  
  addEnvironment: async (environmentData: API.CreateEnvironmentDto) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticEnvironment: API.EnvironmentDto = {
      id: tempId,
      ...environmentData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isLocked: false,
    };

    set(state => ({ environments: [optimisticEnvironment, ...state.environments] }));

    try {
      const created = await api.environments.create(environmentData);
      set(state => ({
        environments: state.environments.map(e => e.id === tempId ? created : e)
      }));
      return created;
    } catch (error) {
      set(state => ({
        environments: state.environments.filter(e => e.id !== tempId),
        error: error instanceof Error ? error.message : 'Failed to create environment'
      }));
      throw error;
    }
  },
  
  updateEnvironment: async (id: string, environmentData: API.UpdateEnvironmentDto) => {
    const original = get().environments.find(e => e.id === id);
    
    set(state => ({
      environments: state.environments.map(e => 
        e.id === id ? { ...e, ...environmentData, updatedAt: new Date().toISOString() } : e
      )
    }));
    
    try {
      const updated = await api.environments.update(id, environmentData);
      set(state => ({
        environments: state.environments.map(e => e.id === id ? updated : e)
      }));
    } catch (error) {
      if (original) {
        set(state => ({
          environments: state.environments.map(e => e.id === id ? original : e)
        }));
      }
      throw error;
    }
  },
  
  deleteEnvironment: async (environmentId: string) => {
    const original = get().environments;
    
    set(state => ({
      environments: state.environments.filter(e => e.id !== environmentId)
    }));
    
    try {
      await api.environments.delete(environmentId);
    } catch (error) {
      set({ environments: original });
      throw error;
    }
  },
  
  getEnvironmentById: async (id: string) => {
    const cached = get().environments.find(e => e.id === id);
    if (cached) return cached;
    
    const data = await api.environments.getById(id);
    set(state => ({
      environments: [...state.environments.filter(e => e.id !== id), data]
    }));
    return data;
  },
  
  hasReachedLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    if (!limits) return true; // No data = assume limit reached
    const envLimit = limits.features?.environments?.limit;
    if (envLimit === undefined) return false; // No limit = never reached
    // Use actual count from store, not backend usage
    const currentCount = get().environments.length;
    return currentCount >= envLimit;
  },
  
  getEnvironmentLimit: () => {
    const { limits } = useSubscriptionStore.getState();
    // Use actual count from store, not backend usage
    const current = get().environments.length;
    const limit = limits?.features?.environments?.limit ?? null;
    const hasReachedLimit = limit !== null && current >= limit;
    return { current, limit, hasReachedLimit };
  },
  
  clearError: () => set({ error: null }),
  }))
);

export const useEnvironmentsStore = environmentsStore;

// Subscribe to account changes and auto-fetch
useAccountStore.subscribe(
  (state) => state.currentAccount,
  (currentAccount) => {
    if (currentAccount?.id) {
      environmentsStore.getState().fetchEnvironments();
    } else {
      environmentsStore.setState({ environments: [] });
    }
  }
);
